"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.KeitaroService = void 0;
const http_1 = __importDefault(require("./http"));
const app_1 = require("../../app/app");
// Default group IDs
const DEFAULT_OFFERS_GROUP_ID = 107;
const DEFAULT_CAMPAIGNS_GROUP_ID = 106;
// Basic campaign operations
async function getStreamsByCampaignId(campaignId) {
    const { data: streams } = await http_1.default.get(`campaigns/${campaignId}/streams`);
    return streams;
}
async function getAllCampaigns() {
    const { data: campaigns } = await http_1.default.get('campaigns');
    return campaigns;
}
async function getCampaignById(id) {
    const { data: campaign } = await http_1.default.get(`/campaigns/${id}`);
    return campaign;
}
async function updateCampaign(id, payload) {
    await http_1.default.put(`campaigns/${id}`, payload);
}
async function createCampaign(campaignData) {
    let { data: campaign } = await http_1.default.post(`/campaigns`, campaignData);
    return campaign;
}
// Domain operations
async function getDomains(onlyActive) {
    const { data: domains } = await http_1.default.get(`/domains`);
    return onlyActive ? domains.filter((d) => d.network_status == "active") : domains;
}
// Offer operations
async function getAllOffers() {
    const { data: offers } = await http_1.default.get('offers');
    return offers;
}
/**
 * Create offer in Keitaro from our IOffer
 * @param offer - Our internal IOffer object
 * @returns Keitaro offer with ID
 */
async function createOfferInKeitaro(offer) {
    const payload = {
        name: `${offer.caption} ${offer.geo} (${offer.name})`,
        group_id: DEFAULT_OFFERS_GROUP_ID,
        action_type: 'http',
        action_payload: offer.link,
        affiliate_network_id: 0,
        payout_value: 0,
        payout_currency: 'USD',
        payout_type: 'CPA',
        state: offer.hidden ? 'disabled' : 'active',
        country: [offer.geo],
        offer_type: 'external',
        payout_auto: true,
        payout_upsell: true,
    };
    const { data: keitaroOffer } = await http_1.default.post('offers', payload);
    return keitaroOffer;
}
// Stream operations
async function createStream(campaignId, streamData) {
    const { data: stream } = await http_1.default.post('streams', {
        ...streamData,
        campaign_id: campaignId
    });
    return stream;
}
/**
 * Get all offerwall campaigns (campaigns with [◈] prefix in group 106)
 */
async function getOfferwallCampaigns() {
    const allCampaigns = await getAllCampaigns();
    // Offerwall campaigns have [◈] in their name and are in group 106
    return allCampaigns.filter(c => c.name.includes('[◈]') && c.group_id === DEFAULT_CAMPAIGNS_GROUP_ID);
}
/**
 * Add our IOffer to Keitaro and distribute to all offerwall campaigns
 * @param offer - Our internal IOffer object
 * @returns Result with Keitaro offer ID and distribution stats
 */
async function addOfferToKeitaro(offer) {
    // First, create the offer in Keitaro
    const keitaroOffer = await createOfferInKeitaro(offer);
    console.log(`Created Keitaro offer #${keitaroOffer.id}: ${keitaroOffer.name}`);
    // Then distribute to all offerwall campaigns
    const offerwallCampaigns = await getOfferwallCampaigns();
    let success = 0;
    let failed = 0;
    const campaignNames = [];
    for (const campaign of offerwallCampaigns) {
        try {
            // Check if stream with this offer already exists
            const existingStreams = await getStreamsByCampaignId(campaign.id);
            const streamExists = existingStreams.some(stream => stream.offers?.some(o => o.offer_id === keitaroOffer.id));
            if (streamExists) {
                console.log(`Stream with offer ${keitaroOffer.id} already exists in campaign ${campaign.name}`);
                continue;
            }
            // Create a new stream with the offer
            await createStream(campaign.id, {
                name: `${offer.geo} - ${offer.caption} (${offer.name})`,
                type: 'regular',
                action_type: 'http',
                weight: 100,
                state: offer.hidden ? 'disabled' : 'active',
                offers: [{
                        offer_id: keitaroOffer.id,
                        share: 100,
                        state: 'active'
                    }],
                filters: [{
                        name: 'country',
                        mode: 'accept',
                        payload: [offer.geo]
                    }]
            });
            success++;
            campaignNames.push(campaign.name);
        }
        catch (error) {
            console.error(`Failed to add offer to campaign ${campaign.name}:`, error);
            failed++;
        }
    }
    return { keitaroOfferId: keitaroOffer.id, success, failed, campaigns: campaignNames };
}
// Generate random alias for campaigns
function generateAlias() {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    return Array.from({ length: 8 }, () => chars.charAt(Math.floor(Math.random() * chars.length))).join('');
}
/**
 * Clone a campaign for an app based on integration type
 * @param app - The app to create campaign for
 * @param integrationType - OFFERWALL or DIRECT
 * @returns Keitaro data for the cloned campaign
 */
async function cloneCampaignForApp(app, integrationType) {
    const templateId = integrationType === app_1.IntegrationType.OFFERWALL
        ? app_1.OFFERWALL_TEMPLATE_CAMPAIGN_ID
        : app_1.DIRECT_TEMPLATE_CAMPAIGN_ID;
    const campaignPrefix = integrationType === app_1.IntegrationType.OFFERWALL ? '[◈]' : '[➥]';
    const campaignName = `#${app.id} ${campaignPrefix} ${app.name || app.bundle}`;
    // Check if campaign already exists
    const allCampaigns = await getAllCampaigns();
    const existingCampaign = allCampaigns.find(c => c.name.includes(`#${app.id}`) && c.name.includes(campaignPrefix));
    if (existingCampaign) {
        return campaignToKeitaroData(existingCampaign);
    }
    // Get template campaign and its streams
    const templateCampaign = await getCampaignById(templateId);
    const templateStreams = await getStreamsByCampaignId(templateId);
    // Get random active domain
    const allDomains = await getDomains(true);
    if (!allDomains || allDomains.length === 0) {
        throw new Error('No active domains available');
    }
    const domain = allDomains[Math.floor(Math.random() * allDomains.length)];
    // Create campaign payload
    const maxPosition = Math.max(...allCampaigns.map(c => c.position || 0));
    const alias = generateAlias();
    const payload = {
        name: campaignName,
        alias: alias,
        domain_id: domain.id,
        position: [maxPosition + 100],
        group_id: DEFAULT_CAMPAIGNS_GROUP_ID,
        type: templateCampaign.type,
        state: templateCampaign.state,
        cost_type: templateCampaign.cost_type,
        cost_value: templateCampaign.cost_value,
        cost_currency: templateCampaign.cost_currency,
        uniqueness_period: templateCampaign.uniqueness_period,
        cookies_ttl: templateCampaign.cookies_ttl,
        notes: templateCampaign.notes,
        collect_clicks: templateCampaign.collect_clicks,
        uniqueness_type: templateCampaign.uniqueness_type,
        parameters: templateCampaign.parameters,
    };
    // Create the new campaign
    const newCampaign = await createCampaign(payload);
    // Clone streams from template
    for (const stream of templateStreams) {
        await http_1.default.post('streams', {
            name: stream.name,
            campaign_id: newCampaign.id,
            schema: stream.schema,
            type: stream.type,
            action_type: stream.action_type,
            weight: stream.weight,
            offers: stream.offers.map((offer) => ({
                offer_id: offer.offer_id,
                share: offer.share,
                state: offer.state
            })),
            filters: stream.filters.map((filter) => ({
                name: filter.name,
                mode: filter.mode,
                payload: filter.payload
            })),
            position: stream.position,
            state: stream.state
        });
    }
    // Get updated campaign with token
    const updatedCampaign = await getCampaignById(newCampaign.id);
    return campaignToKeitaroData(updatedCampaign);
}
/**
 * Convert a Keitaro campaign to IAppKeitaroData
 */
function campaignToKeitaroData(campaign) {
    return {
        trackingCampaignId: campaign.id,
        trackingCampaignName: campaign.name,
        trackingCampaignAlias: campaign.alias,
        trackingDomainId: campaign.domain_id,
        trackingDomainName: campaign.domain || '',
        campingToken: campaign.token,
        trackingParams: {
            naming: campaign.parameters?.sub_id_15?.name || 'naming',
            firebase_app_instance_id: campaign.parameters?.sub_id_11?.name || 'firebase_app_instance_id',
            firebase_user_id: campaign.parameters?.sub_id_12?.name || 'firebase_user_id',
            firebase_device_language_code: campaign.parameters?.sub_id_13?.name || 'firebase_device_language_code',
            firebase_push_token: campaign.parameters?.sub_id_14?.name || 'firebase_push_token',
            bundle_id: campaign.parameters?.sub_id_16?.name || 'bundle_id',
            advertising_id: campaign.parameters?.sub_id_17?.name || 'advertising_id',
            appsflyer_device_id: campaign.parameters?.sub_id_18?.name || 'appsflyer_device_id',
            campaign: campaign.parameters?.sub_id_20?.name || 'campaign',
        }
    };
}
exports.KeitaroService = {
    // Campaign operations
    getAllCampaigns,
    getCampaignById,
    createCampaign,
    updateCampaign,
    cloneCampaignForApp,
    getOfferwallCampaigns,
    // Stream operations
    getStreamsByCampaignId,
    createStream,
    // Domain operations
    getDomains,
    // Offer operations
    getAllOffers,
    createOfferInKeitaro,
    addOfferToKeitaro,
    // Utilities
    generateAlias,
    campaignToKeitaroData,
};
exports.default = exports.KeitaroService;
